import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import AdminPage from "./pages/AdminPage";
import DashboardPage from "./pages/DashboardPage";
import HomePage from "./pages/HomePage";
import PostPage from "./pages/PostPage";
import SearchPage from "./pages/SearchPage";

const ADMIN_EMAIL = "aksh@portfolio.dev";
const ADMIN_PASSWORD = "AkshYadav@STEM";
const AUTH_STORAGE_KEY = "aksh_admin_logged_in";
const UNIQUE_VISITOR_KEY = "aksh_unique_visitor_count";
const VISIT_SESSION_KEY = "aksh_total_sessions_count";
const PAGE_VIEW_KEY = "aksh_total_page_views";
const VISITOR_MARK_KEY = "aksh_unique_visitor_marked";
const SESSION_MARK_KEY = "aksh_session_marked";
const REACTOR_ID_KEY = "aksh_reactor_id";
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://backend.akshyadav.com/api"
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");
const SEARCH_RESULTS_PER_PAGE = 6;

const initialBlog = [
  {
    id: "how-i-structure-portfolio-projects",
    title: "How I Structure Portfolio Projects for Maximum Technical Clarity",
    content:
      "In this blog, I break down how I present my software projects: context, architecture, implementation choices, and measurable outcomes. This approach helps recruiters and collaborators quickly evaluate engineering depth.",
    links: "https://github.com | https://www.linkedin.com",
    images: [],
    likes: 0,
    dislikes: 0,
    userReaction: null,
    bannerFocusX: 50,
    bannerFocusY: 50,
    imageFocusX: 50,
    imageFocusY: 50,
    createdAt: "2026-02-12T11:10:00.000Z",
    updatedAt: null,
  },
  {
    id: "designing-a-futuristic-dashboard",
    title: "Designing a Futuristic Dashboard in React",
    content:
      "This post explores my UI strategy for building clean yet futuristic interfaces. I focus on typographic hierarchy, restrained color systems, motion timing, and a component structure that remains easy to maintain.",
    links: "https://github.com",
    images: [],
    likes: 0,
    dislikes: 0,
    userReaction: null,
    bannerFocusX: 50,
    bannerFocusY: 50,
    imageFocusX: 50,
    imageFocusY: 50,
    createdAt: "2026-02-16T08:30:00.000Z",
    updatedAt: null,
  },
  {
    id: "ai-suggestor-patterns-for-faster-blogging",
    title: "AI Suggestor Patterns for Faster Technical Blogging",
    content:
      "To reduce writing friction, I built a topic-aware AI suggestor that generates structured opening drafts. The system helps me start quickly while preserving technical specificity and my own voice.",
    links: "https://openai.com",
    images: [],
    likes: 0,
    dislikes: 0,
    userReaction: null,
    bannerFocusX: 50,
    bannerFocusY: 50,
    imageFocusX: 50,
    imageFocusY: 50,
    createdAt: "2026-02-21T17:05:00.000Z",
    updatedAt: null,
  },
];

const toAbsoluteImageUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith("/") ? url : `/${url}`}`;
};

const normalizeInlineTokenUrls = (content) =>
  String(content || "").replace(/\[\[img:([^\]]+)\]\]/g, (fullMatch, rawUrl) => {
    const value = String(rawUrl || "").trim();
    if (!value) return fullMatch;
    if (!value.startsWith("/") && !/^https?:\/\//i.test(value)) return fullMatch;
    return `[[img:${toAbsoluteImageUrl(value)}]]`;
  });

const normalizeBlog = (blog) => ({
  id: blog.id || blog._id,
  title: blog.title || "",
  content: normalizeInlineTokenUrls(blog.content || ""),
  links: blog.links || "",
  categories: Array.isArray(blog.categories)
    ? blog.categories.map((item) => String(item || "").trim()).filter(Boolean)
    : String(blog.categories || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
  bannerImage: toAbsoluteImageUrl(blog.bannerImage || ""),
  images: Array.isArray(blog.images) ? blog.images.map(toAbsoluteImageUrl).filter(Boolean) : [],
  likes: Number.isFinite(Number(blog.likes)) ? Number(blog.likes) : 0,
  dislikes: Number.isFinite(Number(blog.dislikes)) ? Number(blog.dislikes) : 0,
  userReaction:
    blog.userReaction === "like" || blog.userReaction === "dislike" ? blog.userReaction : null,
  bannerFocusX: Number.isFinite(Number(blog.bannerFocusX)) ? Number(blog.bannerFocusX) : 50,
  bannerFocusY: Number.isFinite(Number(blog.bannerFocusY)) ? Number(blog.bannerFocusY) : 50,
  imageFocusX: Number.isFinite(Number(blog.imageFocusX)) ? Number(blog.imageFocusX) : 50,
  imageFocusY: Number.isFinite(Number(blog.imageFocusY)) ? Number(blog.imageFocusY) : 50,
  createdAt: blog.createdAt || new Date().toISOString(),
  updatedAt: blog.updatedAt || null,
});

const stripHtmlForSnippet = (value) =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\[\[img:[^\]]+\]\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getOrCreateReactorId = () => {
  if (typeof window === "undefined") return "";
  const existing = String(localStorage.getItem(REACTOR_ID_KEY) || "").trim();
  if (/^[A-Za-z0-9_-]{8,80}$/.test(existing)) return existing;

  let generated = "";
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    generated = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  } else {
    generated = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 18)}`;
  }
  const reactorId = `v_${generated}`.slice(0, 64);
  localStorage.setItem(REACTOR_ID_KEY, reactorId);
  return reactorId;
};

const getRouteFromPath = (pathname) => {
  if (pathname === "/search") {
    return { page: "search", activePostId: null };
  }
  if (pathname === "/admin") {
    return { page: "admin", activePostId: null };
  }
  if (pathname === "/dashboard") {
    return { page: "dashboard", activePostId: null };
  }
  if (pathname.startsWith("/posts/")) {
    const postId = decodeURIComponent(pathname.replace("/posts/", ""));
    return { page: "post", activePostId: postId || null };
  }
  return { page: "home", activePostId: null };
};

const getInitialRoute = () => {
  if (typeof window === "undefined") {
    return { page: "home", activePostId: null };
  }
  return getRouteFromPath(window.location.pathname);
};

function App() {
  const [route, setRoute] = useState(getInitialRoute);
  const [showNavbar, setShowNavbar] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authError, setAuthError] = useState("");
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [blogsError, setBlogsError] = useState("");
  const [publishError, setPublishError] = useState("");
  const [publishSuccess, setPublishSuccess] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", categories: "", content: "", links: "" });
  const [newImages, setNewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [newBannerImage, setNewBannerImage] = useState(null);
  const [existingBannerImage, setExistingBannerImage] = useState("");
  const [inlineImageUploads, setInlineImageUploads] = useState([]);
  const [bannerFocus, setBannerFocus] = useState({ x: 50, y: 50 });
  const [imageFocus, setImageFocus] = useState({ x: 50, y: 50 });
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSuggestError, setAiSuggestError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const [visitorStats, setVisitorStats] = useState({
    uniqueVisitors: 0,
    totalSessions: 0,
    totalPageViews: 0,
  });
  const [reactorId, setReactorId] = useState("");
  const lastScrollYRef = useRef(0);
  const newImagesRef = useRef([]);
  const { page, activePostId } = route;

  const sortedBlogs = useMemo(
    () =>
      [...blogs].sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt).getTime() -
          new Date(a.updatedAt || a.createdAt).getTime()
      ),
    [blogs]
  );
  const activePost = blogs.find((blog) => blog.id === activePostId) || null;
  const sequenceSuggestions = useMemo(() => {
    if (!activePostId || sortedBlogs.length <= 1) return [];
    const currentIndex = sortedBlogs.findIndex((blog) => blog.id === activePostId);
    if (currentIndex === -1) return [];

    const suggestions = [];
    for (let step = 1; step < Math.min(4, sortedBlogs.length); step += 1) {
      const nextIndex = (currentIndex + step) % sortedBlogs.length;
      suggestions.push(sortedBlogs[nextIndex]);
    }
    return suggestions;
  }, [activePostId, sortedBlogs]);

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredSearchBlogs = useMemo(() => {
    if (!normalizedSearchTerm) return sortedBlogs;
    const queryWords = normalizedSearchTerm.split(/\s+/).filter(Boolean);
    if (!queryWords.length) return sortedBlogs;
    return sortedBlogs.filter((blog) => {
      const titleText = String(blog.title || "").toLowerCase();
      return queryWords.every((word) => titleText.includes(word));
    });
  }, [sortedBlogs, normalizedSearchTerm]);

  const totalSearchPages = Math.max(
    1,
    Math.ceil(filteredSearchBlogs.length / SEARCH_RESULTS_PER_PAGE)
  );
  const paginatedSearchBlogs = useMemo(() => {
    const startIndex = (searchPage - 1) * SEARCH_RESULTS_PER_PAGE;
    return filteredSearchBlogs.slice(startIndex, startIndex + SEARCH_RESULTS_PER_PAGE);
  }, [filteredSearchBlogs, searchPage]);

  const getSnippet = (content) => {
    const plain = stripHtmlForSnippet(content);
    if (plain.length <= 170) return plain;
    return `${plain.slice(0, 170).trim()}...`;
  };

  const readApiError = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      if (data?.message) return String(data.message);
    } catch (_error) {
      // Ignore JSON parse errors and use fallback.
    }
    return fallbackMessage;
  };

  const resetForm = useCallback(() => {
    newImagesRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
    if (newBannerImage?.preview) URL.revokeObjectURL(newBannerImage.preview);
    inlineImageUploads.forEach((item) => URL.revokeObjectURL(item.preview));
    setForm({ title: "", categories: "", content: "", links: "" });
    setNewImages([]);
    setExistingImages([]);
    setExistingBannerImage("");
    setNewBannerImage(null);
    setInlineImageUploads([]);
    setAiPrompt("");
    setAiSuggestError("");
    setBannerFocus({ x: 50, y: 50 });
    setImageFocus({ x: 50, y: 50 });
    setEditingId(null);
  }, [inlineImageUploads, newBannerImage]);

  const syncRouteFromLocation = useCallback(() => {
    setRoute(getRouteFromPath(window.location.pathname));
  }, []);

  const navigateTo = useCallback(
    (path) => {
      if (window.location.pathname !== path) {
        window.history.pushState({}, "", path);
      }
      syncRouteFromLocation();
    },
    [syncRouteFromLocation]
  );

  const fetchBlogs = useCallback(async () => {
    setBlogsLoading(true);
    setBlogsError("");
    try {
      const url = reactorId
        ? `${API_BASE_URL}/blogs?viewerId=${encodeURIComponent(reactorId)}`
        : `${API_BASE_URL}/blogs`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to load blogs");
      }
      const data = await response.json();
      const items = Array.isArray(data) && data.length ? data : initialBlog;
      setBlogs(items.map(normalizeBlog));
    } catch (_error) {
      setBlogs(initialBlog);
      setBlogsError("Backend unavailable. Showing local sample posts.");
    } finally {
      setBlogsLoading(false);
    }
  }, [reactorId]);

  const goHome = () => navigateTo("/");
  const goSearch = () => navigateTo("/search");
  const goAdmin = () => navigateTo("/admin");
  const goDashboard = () => navigateTo("/dashboard");
  const openPost = (id) => navigateTo(`/posts/${encodeURIComponent(id)}`);

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    navigateTo("/");
    resetForm();
  };

  const handleLogin = (event) => {
    event.preventDefault();
    if (credentials.email !== ADMIN_EMAIL || credentials.password !== ADMIN_PASSWORD) {
      setAuthError("Invalid admin credentials.");
      return;
    }
    setAuthError("");
    setIsLoggedIn(true);
    localStorage.setItem(AUTH_STORAGE_KEY, "true");
    setCredentials({ email: "", password: "" });
    navigateTo("/dashboard");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPublishError("");
    setPublishSuccess("");
    const payload = {
      title: form.title.trim(),
      categories: form.categories.trim(),
      content: form.content.trim(),
      links: form.links.trim(),
    };
    if (!payload.title || !payload.content) {
      setPublishError("Title and content are required.");
      return;
    }

    try {
      setIsPublishing(true);
      const endpoint = editingId
        ? `${API_BASE_URL}/blogs/${encodeURIComponent(editingId)}`
        : `${API_BASE_URL}/blogs`;
      const method = editingId ? "PUT" : "POST";
      const body = new FormData();
      body.append("title", payload.title);
      body.append("categories", payload.categories);
      body.append("content", payload.content);
      body.append("links", payload.links);
      body.append("existingImages", JSON.stringify(existingImages));
      body.append("existingBannerImage", existingBannerImage || "");
      body.append(
        "inlineImageKeys",
        JSON.stringify(inlineImageUploads.map((item) => item.key))
      );
      body.append("bannerFocusX", String(bannerFocus.x));
      body.append("bannerFocusY", String(bannerFocus.y));
      body.append("imageFocusX", String(imageFocus.x));
      body.append("imageFocusY", String(imageFocus.y));
      newImages.forEach((item) => body.append("images", item.file));
      inlineImageUploads.forEach((item) => body.append("inlineImages", item.file));
      if (newBannerImage?.file) body.append("bannerImage", newBannerImage.file);

      const response = await fetch(endpoint, {
        method,
        body,
      });

      if (!response.ok) {
        const apiMessage = await readApiError(response, "Unable to save blog.");
        throw new Error(apiMessage);
      }

      const saved = normalizeBlog(await response.json());
      if (editingId) {
        setBlogs((current) =>
          current.map((blog) => (blog.id === editingId ? saved : blog))
        );
      } else {
        setBlogs((current) => [saved, ...current]);
      }
      setPublishSuccess(editingId ? "Post updated successfully." : "Post published successfully.");
      resetForm();
    } catch (error) {
      setPublishError(error?.message || "Could not publish the post.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleEdit = (blog) => {
    if (!isLoggedIn) return;
    setPublishError("");
    setPublishSuccess("");
    newImages.forEach((item) => URL.revokeObjectURL(item.preview));
    navigateTo("/admin");
    setEditingId(blog.id);
    setForm({
      title: blog.title,
      categories: Array.isArray(blog.categories) ? blog.categories.join(", ") : "",
      content: blog.content,
      links: blog.links,
    });
    setExistingImages(Array.isArray(blog.images) ? blog.images : []);
    setExistingBannerImage(blog.bannerImage || "");
    setAiPrompt(blog.title || "");
    setBannerFocus({
      x: Number.isFinite(blog.bannerFocusX) ? blog.bannerFocusX : 50,
      y: Number.isFinite(blog.bannerFocusY) ? blog.bannerFocusY : 50,
    });
    setImageFocus({
      x: Number.isFinite(blog.imageFocusX) ? blog.imageFocusX : 50,
      y: Number.isFinite(blog.imageFocusY) ? blog.imageFocusY : 50,
    });
    setAiSuggestError("");
    if (newBannerImage?.preview) URL.revokeObjectURL(newBannerImage.preview);
    inlineImageUploads.forEach((item) => URL.revokeObjectURL(item.preview));
    setNewBannerImage(null);
    setInlineImageUploads([]);
    setNewImages([]);
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Unable to delete blog");
      }
      setBlogs((current) => current.filter((blog) => blog.id !== id));
      if (editingId === id) resetForm();
      if (activePostId === id) goHome();
    } catch (_error) {
      setBlogsError("Could not delete blog from MongoDB right now.");
    }
  };

  const handleReactToBlog = async (id, type) => {
    const blogId = String(id || "").trim();
    const reactionType = String(type || "").trim().toLowerCase();
    if (!blogId || (reactionType !== "like" && reactionType !== "dislike")) {
      throw new Error("Invalid reaction request.");
    }

    const currentReactorId = reactorId || getOrCreateReactorId();
    if (!reactorId && currentReactorId) {
      setReactorId(currentReactorId);
    }
    const response = await fetch(`${API_BASE_URL}/blogs/${encodeURIComponent(blogId)}/reaction`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: reactionType, viewerId: currentReactorId }),
    });
    if (!response.ok) {
      const apiMessage = await readApiError(response, "Could not submit reaction.");
      throw new Error(apiMessage);
    }

    const payload = await response.json();
    const likes = Number.isFinite(Number(payload?.likes)) ? Number(payload.likes) : 0;
    const dislikes = Number.isFinite(Number(payload?.dislikes)) ? Number(payload.dislikes) : 0;
    const userReaction =
      payload?.userReaction === "like" || payload?.userReaction === "dislike"
        ? payload.userReaction
        : null;
    setBlogs((current) =>
      current.map((blog) =>
        blog.id === blogId ? { ...blog, likes, dislikes, userReaction } : blog
      )
    );
    return { likes, dislikes, userReaction };
  };

  const handleImageSelect = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;
    const mapped = selected.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setNewImages((current) => [...current, ...mapped]);
    event.target.value = "";
  };

  const removeNewImage = (index) => {
    setNewImages((current) => {
      const target = current[index];
      if (target) URL.revokeObjectURL(target.preview);
      return current.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (index) => {
    setExistingImages((current) => current.filter((_, i) => i !== index));
  };

  const handleBannerImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (newBannerImage?.preview) URL.revokeObjectURL(newBannerImage.preview);
    setNewBannerImage({
      file,
      preview: URL.createObjectURL(file),
    });
    event.target.value = "";
  };

  const removeNewBannerImage = () => {
    if (newBannerImage?.preview) URL.revokeObjectURL(newBannerImage.preview);
    setNewBannerImage(null);
  };

  const removeInlineImageUpload = (index) => {
    setInlineImageUploads((current) => {
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      if (target?.key) {
        setForm((draft) => ({
          ...draft,
          content: draft.content.replaceAll(`[[img:${target.key}]]`, ""),
        }));
      }
      return current.filter((_, i) => i !== index);
    });
  };

  const handleInlineImageInsert = (fileList, selectionStart, selectionEnd) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const created = files.map((file, index) => {
      const key = `img_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`;
      return {
        key,
        file,
        preview: URL.createObjectURL(file),
      };
    });

    const insertedText = created.map((item) => `\n[[img:${item.key}]]\n`).join("");
    setInlineImageUploads((current) => [...current, ...created]);
    setForm((current) => {
      const content = current.content || "";
      const safeStart = Number.isInteger(selectionStart) ? selectionStart : content.length;
      const safeEnd = Number.isInteger(selectionEnd) ? selectionEnd : safeStart;
      return {
        ...current,
        content: `${content.slice(0, safeStart)}${insertedText}${content.slice(safeEnd)}`,
      };
    });
  };

  const handleSuggestDraft = () => {
    const prompt = aiPrompt.trim();
    if (!prompt) {
      setAiSuggestError("Enter a topic or prompt to get a draft suggestion.");
      return;
    }

    setAiSuggestError("");
    const cleanPrompt = prompt
      .replace(/\s+/g, " ")
      .trim();
    const suggestedTitle = `What I Learned While Working On ${cleanPrompt}`;
    const suggestedContent = [
      `This draft is based on the topic "${cleanPrompt}".`,
      "",
      "Introduction",
      `I started exploring ${cleanPrompt} with a practical, execution-first mindset. This post documents what worked, what did not, and what I would do differently next time.`,
      "",
      "Key Insights",
      "- Define the goal clearly before starting.",
      "- Break the process into small testable steps.",
      "- Measure progress consistently and adjust quickly.",
      "",
      "Action Plan",
      "1. Set a realistic baseline and timeline.",
      "2. Build a repeatable workflow you can sustain.",
      "3. Track outcomes weekly and refine your approach.",
      "",
      "Final Thoughts",
      `If you are working on ${cleanPrompt}, focus on clarity, consistency, and iteration. Long-term results come from small high-quality decisions made regularly.`,
    ].join("\n");

    setForm((current) => ({
      ...current,
      title: current.title.trim() ? current.title : suggestedTitle,
      content: suggestedContent,
    }));
  };

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;

      if (currentY < 30) {
        setShowNavbar(true);
      } else if (delta > 2) {
        setShowNavbar(false);
      } else if (delta < -2) {
        setShowNavbar(true);
      }

      lastScrollYRef.current = currentY;
    };

    syncRouteFromLocation();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("popstate", syncRouteFromLocation);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("popstate", syncRouteFromLocation);
    };
  }, [syncRouteFromLocation]);

  useEffect(() => {
    const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY) === "true";
    if (storedAuth) {
      setIsLoggedIn(true);
    }

    const syncAuthAcrossTabs = (event) => {
      if (event.key === AUTH_STORAGE_KEY) {
        setIsLoggedIn(event.newValue === "true");
        if (event.newValue !== "true") {
          navigateTo("/");
          resetForm();
        }
      }
    };

    window.addEventListener("storage", syncAuthAcrossTabs);
    return () => window.removeEventListener("storage", syncAuthAcrossTabs);
  }, [navigateTo, resetForm]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentUnique = Number(localStorage.getItem(UNIQUE_VISITOR_KEY) || "0");
    const currentSessions = Number(localStorage.getItem(VISIT_SESSION_KEY) || "0");
    const currentViews = Number(localStorage.getItem(PAGE_VIEW_KEY) || "0");

    let nextUnique = currentUnique;
    let nextSessions = currentSessions;
    if (!localStorage.getItem(VISITOR_MARK_KEY)) {
      nextUnique += 1;
      localStorage.setItem(VISITOR_MARK_KEY, "true");
      localStorage.setItem(UNIQUE_VISITOR_KEY, String(nextUnique));
    }
    if (!sessionStorage.getItem(SESSION_MARK_KEY)) {
      nextSessions += 1;
      sessionStorage.setItem(SESSION_MARK_KEY, "true");
      localStorage.setItem(VISIT_SESSION_KEY, String(nextSessions));
    }
    setVisitorStats({
      uniqueVisitors: nextUnique,
      totalSessions: nextSessions,
      totalPageViews: currentViews,
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const currentViews = Number(localStorage.getItem(PAGE_VIEW_KEY) || "0") + 1;
    localStorage.setItem(PAGE_VIEW_KEY, String(currentViews));
    setVisitorStats((current) => ({ ...current, totalPageViews: currentViews }));
  }, [page, activePostId]);

  useEffect(() => {
    setReactorId(getOrCreateReactorId());
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  useEffect(() => {
    setSearchPage(1);
  }, [normalizedSearchTerm]);

  useEffect(() => {
    if (searchPage > totalSearchPages) {
      setSearchPage(totalSearchPages);
    }
  }, [searchPage, totalSearchPages]);

  useEffect(() => {
    newImagesRef.current = newImages;
  }, [newImages]);

  useEffect(
    () => () => {
      newImagesRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
    },
    []
  );

  useEffect(() => {
    if (!isLoggedIn && page === "admin") {
      setEditingId(null);
    }
  }, [isLoggedIn, page]);

  useEffect(() => {
    if (!isLoggedIn && page === "dashboard") {
      navigateTo("/admin");
    }
  }, [isLoggedIn, page, navigateTo]);

  const dashboardStats = useMemo(() => {
    const now = Date.now();
    const blogsWithTime = sortedBlogs
      .map((blog) => ({
        ...blog,
        ts: new Date(blog.updatedAt || blog.createdAt).getTime(),
      }))
      .filter((blog) => Number.isFinite(blog.ts))
      .sort((a, b) => a.ts - b.ts);

    const totalBlogs = sortedBlogs.length;
    const totalLikes = sortedBlogs.reduce(
      (sum, blog) => sum + (Number.isFinite(blog.likes) ? blog.likes : 0),
      0
    );
    const totalDislikes = sortedBlogs.reduce(
      (sum, blog) => sum + (Number.isFinite(blog.dislikes) ? blog.dislikes : 0),
      0
    );
    const totalWords = sortedBlogs.reduce(
      (sum, blog) =>
        sum + stripHtmlForSnippet(blog.content || "").split(/\s+/).filter(Boolean).length,
      0
    );
    const avgWordsPerPost = totalBlogs ? Math.round(totalWords / totalBlogs) : 0;
    const activeCategories = new Set(
      sortedBlogs.flatMap((blog) =>
        Array.isArray(blog.categories) && blog.categories.length ? blog.categories : ["General"]
      )
    ).size;
    const monthMs = 1000 * 60 * 60 * 24 * 30;
    const blogsThisMonth = sortedBlogs.filter((blog) => {
      const ts = new Date(blog.createdAt).getTime();
      return Number.isFinite(ts) && now - ts <= monthMs;
    }).length;
    const daysBetween = blogsWithTime.slice(1).map((blog, index) => {
      const prev = blogsWithTime[index];
      return (blog.ts - prev.ts) / (1000 * 60 * 60 * 24);
    });
    const avgDaysBetweenPosts = daysBetween.length
      ? Math.round(daysBetween.reduce((sum, value) => sum + value, 0) / daysBetween.length)
      : 0;
    const frequencyPerMonth = avgDaysBetweenPosts
      ? Number((30 / avgDaysBetweenPosts).toFixed(1))
      : totalBlogs
        ? Number((totalBlogs / 1).toFixed(1))
        : 0;

    const uniqueVisitors = visitorStats.uniqueVisitors || 0;
    const totalSessions = visitorStats.totalSessions || 0;
    const pageViews = visitorStats.totalPageViews || 0;
    const returningVisitors = Math.max(0, totalSessions - uniqueVisitors);
    const engagementRate = uniqueVisitors
      ? Math.min(99, Math.round((pageViews / uniqueVisitors) * 18))
      : 0;
    const retentionRate = uniqueVisitors
      ? Math.min(95, Math.round((returningVisitors / uniqueVisitors) * 100))
      : 0;
    const avgSessionDepth = totalSessions ? Number((pageViews / totalSessions).toFixed(2)) : 0;

    const topCategories = Object.entries(
      sortedBlogs.reduce((acc, blog) => {
        const tags =
          Array.isArray(blog.categories) && blog.categories.length
            ? blog.categories
            : ["General"];
        tags.forEach((tag) => {
          acc[tag] = (acc[tag] || 0) + 1;
        });
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4);

    const recentPosts = sortedBlogs.slice(0, 5).map((blog) => ({
      id: blog.id,
      title: blog.title,
      date: new Date(blog.updatedAt || blog.createdAt).toLocaleDateString(),
    }));
    const mostLikedPosts = [...sortedBlogs]
      .sort((a, b) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 5)
      .map((blog) => ({
        id: blog.id,
        title: blog.title,
        likes: Number.isFinite(blog.likes) ? blog.likes : 0,
        dislikes: Number.isFinite(blog.dislikes) ? blog.dislikes : 0,
      }));

    return {
      totalBlogs,
      totalLikes,
      totalDislikes,
      blogsThisMonth,
      activeCategories,
      avgWordsPerPost,
      frequencyPerMonth,
      avgDaysBetweenPosts,
      uniqueVisitors,
      totalSessions,
      pageViews,
      engagementRate,
      retentionRate,
      avgSessionDepth,
      topCategories,
      recentPosts,
      mostLikedPosts,
    };
  }, [sortedBlogs, visitorStats]);

  const currentYear = new Date().getFullYear();
  const focusAreas = [
    {
      title: "Build Philosophy",
      detail:
        "I like products that feel effortless but are backed by strong engineering discipline, clean architecture, and thoughtful iteration.",
    },
    {
      title: "Creative Interests",
      detail:
        "I explore interface design, storytelling through writing, and visual systems that make technical ideas easier to understand.",
    },
    {
      title: "Personal Drive",
      detail:
        "Consistency matters to me. Whether it is fitness, coding, or writing, I focus on process quality and long-term progress.",
    },
  ];

  const homeHighlights = [
    "Detailed build logs with architecture decisions",
    "Personal notes on habits, learning, and execution",
    "Project links and actionable takeaways in every post",
  ];

  return (
    <div className="site">
      <Navbar
        showNavbar={showNavbar}
        page={page}
        isLoggedIn={isLoggedIn}
        goHome={goHome}
        goSearch={goSearch}
        goAdmin={goAdmin}
        goDashboard={goDashboard}
        handleLogout={handleLogout}
      />

      {page === "home" && (
        <HomePage
          focusAreas={focusAreas}
          homeHighlights={homeHighlights}
          blogsError={blogsError}
          blogsLoading={blogsLoading}
          sortedBlogs={sortedBlogs}
          getSnippet={getSnippet}
          openPost={openPost}
        />
      )}

      {page === "search" && (
        <SearchPage
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredSearchBlogs={filteredSearchBlogs}
          normalizedSearchTerm={normalizedSearchTerm}
          paginatedSearchBlogs={paginatedSearchBlogs}
          getSnippet={getSnippet}
          openPost={openPost}
          totalSearchPages={totalSearchPages}
          searchPage={searchPage}
          setSearchPage={setSearchPage}
        />
      )}

      {page === "post" && (
        <PostPage
          activePost={activePost}
          blogsLoading={blogsLoading}
          isLoggedIn={isLoggedIn}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          sequenceSuggestions={sequenceSuggestions}
          openPost={openPost}
          goHome={goHome}
          onReact={handleReactToBlog}
        />
      )}

      {page === "admin" && (
        <AdminPage
          isLoggedIn={isLoggedIn}
          editingId={editingId}
          handleLogout={handleLogout}
          handleLogin={handleLogin}
          credentials={credentials}
          setCredentials={setCredentials}
          authError={authError}
          form={form}
          setForm={setForm}
          aiPrompt={aiPrompt}
          setAiPrompt={setAiPrompt}
          aiSuggestError={aiSuggestError}
          handleSuggestDraft={handleSuggestDraft}
          publishError={publishError}
          publishSuccess={publishSuccess}
          isPublishing={isPublishing}
          handleSubmit={handleSubmit}
          existingBannerImage={existingBannerImage}
          setExistingBannerImage={setExistingBannerImage}
          newBannerImage={newBannerImage}
          handleBannerImageSelect={handleBannerImageSelect}
          removeNewBannerImage={removeNewBannerImage}
          bannerFocus={bannerFocus}
          setBannerFocus={setBannerFocus}
          imageFocus={imageFocus}
          setImageFocus={setImageFocus}
          inlineImageUploads={inlineImageUploads}
          handleInlineImageInsert={handleInlineImageInsert}
          removeInlineImageUpload={removeInlineImageUpload}
          handleImageSelect={handleImageSelect}
          existingImages={existingImages}
          removeExistingImage={removeExistingImage}
          newImages={newImages}
          removeNewImage={removeNewImage}
          resetForm={resetForm}
        />
      )}

      {page === "dashboard" && isLoggedIn && (
        <DashboardPage
          stats={dashboardStats}
          openPost={openPost}
          goAdmin={goAdmin}
        />
      )}

      <Footer currentYear={currentYear} />
    </div>
  );
}

export default App;
